import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';

/**
 * Malzeme kataloğu (ADR 0026 §1, §4). Katalog ofisin verisidir; gemide salt okunur. Listeler
 * müşterinin `BGM_Material_Master_5000.xlsx` dosyasındaki değerlerdir (`malzeme-katalogu.md`).
 * Hata iletileri koddur; arayüz çevirir.
 */

/**
 * Ölçü birimleri (UOM): dosyadaki altı birim (`TIN` yalnız iki boya kaleminde ama korunur) ve
 * kumanya kütüphanesinin birimleri (ADR 0099 §3; kg→KG, L→LTR, pcs→PCS eşlenir).
 */
export const MATERIAL_UNITS = [
  'PCS',
  'MTR',
  'KG',
  'LTR',
  'SET',
  'TIN',
  'G',
  'ML',
  'PACK',
  'CAN',
  'JAR',
  'BTL',
  'BUNCH',
  'TUB',
  'BOX',
  'ROLL',
] as const;
export type MaterialUnit = (typeof MATERIAL_UNITS)[number];

/** Malzeme türleri (Material Type); `Provision` kumanya kalemidir (ADR 0099 §4). */
export const MATERIAL_TYPES = ['Consumable', 'Spare Part', 'Equipment/Spare', 'Provision'] as const;
export type MaterialType = (typeof MATERIAL_TYPES)[number];

/**
 * Malzeme sınıfı (ADR 0122 §1): tek katalog ve tek stok tablosunda iki havuz. `provision` kumanya
 * (Inventory ▸ Provision; ADR 0099), `stores` yedek parça, sarf ve gemi malzemesi (Inventory ▸
 * Spare Parts). Sınıf ayrı bir kolon değildir; malzeme türünden (`material_type`) türer.
 */
export const MATERIAL_CLASSES = ['provision', 'stores'] as const;
export type MaterialClass = (typeof MATERIAL_CLASSES)[number];

/** Malzeme türünün sınıfı: yalnız `Provision` kumanyadır; bilinmeyen tür stores sayılır. */
export function materialClassOf(materialType: string): MaterialClass {
  return materialType === 'Provision' ? 'provision' : 'stores';
}

/**
 * IMPA Marine Stores Guide'ın 00 bölümü kumanyadır (Provisions; kodlar `00 xx xx`). Boşluk ve
 * nokta yok sayılır; altı haneli olmayan kod bölüm vermez.
 */
export function isProvisionImpa(code: string | null | undefined): boolean {
  const digits = (code ?? '').replace(/[\s.]/g, '');
  return /^00\d{4}$/.test(digits);
}

/** Ana kategori adı (baştaki numara atılarak) "Provision" ya da "Provisions". */
export function isProvisionCategory(mainCategory: string | null | undefined): boolean {
  const name = normalizeKeyPart(mainCategory).replace(/^\d+\s*/, '');
  return name === 'provision' || name === 'provisions';
}

/**
 * Yeni kalemin önerilen türü (ADR 0122 §2): IMPA 00 bölümü ya da "Provisions" ana kategorisi
 * `Provision`; yoksa öneri yok (kullanıcı ya da dosya türü verir).
 */
export function suggestedMaterialType(input: {
  impaCode?: string | null;
  mainCategory?: string | null;
}): MaterialType | null {
  return isProvisionImpa(input.impaCode) || isProvisionCategory(input.mainCategory)
    ? 'Provision'
    : null;
}

/** Anahtar parçası: baştaki ve sondaki boşluk atılır, ardışık boşluk teke iner, küçük harf. */
export function normalizeKeyPart(value: string | null | undefined): string {
  return collapse(value ?? '').toLowerCase();
}

function collapse(value: string): string {
  return value.normalize('NFC').trim().replace(/\s+/g, ' ');
}

export interface MaterialKeyParts {
  name: string;
  specification?: string | null;
}

/**
 * Kalem anahtarı: ad ve özellik (ADR 0018 §6; ADR 0026 §4 ve uygulama notları). Kategori
 * anahtara girmez: iki kategoride geçen kalem tek kalemdir, birincil kategori ilk geçtiği,
 * diğeri ikinci kategoridir. Aynı anahtar ikinci içe aktarmada güncellenir, yeni kalem açılmaz.
 * Parçalar JSON dizisi olarak birleşir; ayraç içeren bir ad başka bir kalemle çakışmaz. Boş
 * özellik ile özelliksiz kalem aynıdır.
 */
export function materialKey(parts: MaterialKeyParts): string {
  return JSON.stringify([normalizeKeyPart(parts.name), normalizeKeyPart(parts.specification)]);
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});

const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

/** İsteğe bağlı metin: boş metin `null` olur. */
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish();

/**
 * Çok satırlı isteğe bağlı metin (not): satır sonları korunur; her satırda boşluklar düzeltilir,
 * boş satırlar atılır (M6f G5-12). Boş metin `null` olur.
 */
const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish();

const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));

/**
 * Kalem (Material Detail). Sistem kimliği UUIDv7'dir; şirket kodu, IMPA ve ISSA isteğe bağlı.
 * İkinci kategori, kalemin geçtiği öbür kategoridir (ADR 0018 §6); birinciyle aynı olamaz.
 */
export const materialSchema = z
  .object({
    categoryId: z.uuid(typed('INVALID_ID')),
    secondaryCategoryId: z.uuid(typed('INVALID_ID')).nullish(),
    name: text(200),
    specification: optionalText(200),
    unit: option(MATERIAL_UNITS),
    materialType: option(MATERIAL_TYPES),
    companyCode: optionalText(60),
    impaCode: optionalText(20),
    issaCode: optionalText(20),
    critical: z.boolean(typed('INVALID_BOOLEAN')).default(false),
    notes: optionalLines(2000),
  })
  .superRefine((material, ctx) => {
    if (material.secondaryCategoryId && material.secondaryCategoryId === material.categoryId) {
      ctx.addIssue({ code: 'custom', message: 'SAME_CATEGORY', path: ['secondaryCategoryId'] });
    }
  });
export type Material = z.output<typeof materialSchema>;

/**
 * Kategori: ana kategori (`01 Deck & Mooring`) ve alt kategori (`Mooring Ropes`) çifti; sıra ana
 * kategorinin numarasından (`malzeme-katalogu.md`).
 */
export const materialCategorySchema = z.object({
  mainCategory: text(200),
  subCategory: text(200),
  sortOrder: z.number(typed('INVALID_NUMBER')).int('INVALID_NUMBER').min(0, 'OUT_OF_RANGE'),
});
export type MaterialCategory = z.output<typeof materialCategorySchema>;

/** Kategori çiftinin anahtarı: boşluk ve harf büyüklüğü fark etmez. */
export function categoryKey(mainCategory: string, subCategory: string): string {
  return JSON.stringify([normalizeKeyPart(mainCategory), normalizeKeyPart(subCategory)]);
}

/** Ana kategorinin baştaki numarası (`01 Deck & Mooring` → 1); yoksa `null`. */
export function categoryNumber(mainCategory: string): number | null {
  const match = /^\s*(\d+)\b/.exec(mainCategory);
  return match ? Number(match[1]) : null;
}

export function parseMaterialCategory(input: unknown): ParseResult<MaterialCategory> {
  const result = materialCategorySchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export function toIssues(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

export function parseMaterial(input: unknown): ParseResult<Material> {
  const result = materialSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}
